import React, { useEffect, useMemo, useState } from 'react';
import { Table, Button, Space, message, Typography } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { invoiceService, Invoice } from '../services/invoices';
import { useTableColumnSearch } from '../hooks/useTableColumnSearch';
import { useResizableColumns } from '../hooks/useResizableColumns';
import { useColumnVisibility } from '../hooks/useColumnVisibility';
import { ResizableTableHeader } from '../components/ResizableTableHeader';
import { ColumnVisibilityToggle } from '../components/ColumnVisibilityToggle';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';
import '../styles/resizable-table.css';

const { Title } = Typography;

export const Invoices: React.FC = () => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const navigate = useNavigate();
  const { user } = useAuth();
  const canView = hasPermission(user, 'invoices.view');

  useEffect(() => {
    if (!canView) {
      setInvoices([]);
      return;
    }
    fetchData();
  }, [canView]);

  const fetchData = async () => {
    if (!canView) {
      return;
    }
    setLoading(true);
    try {
      const response = await invoiceService.getAll();
      setInvoices(response.data || []);
    } catch {
      message.error('Failed to load invoices data');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await invoiceService.delete(id);
      message.success('Invoice deleted');
      fetchData();
    } catch {
      message.error('Failed to delete invoice');
    }
  };

  const baseColumns = [
    { 
      title: 'Invoice Number', 
      dataIndex: 'invoice_number', 
      key: 'invoice_number', 
      width: 180,
      sorter: (a: Invoice, b: Invoice) => (a.invoice_number || '').localeCompare(b.invoice_number || ''),
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: 120,
      sorter: (a: Invoice, b: Invoice) => {
        if (!a.date && !b.date) return 0;
        if (!a.date) return 1;
        if (!b.date) return -1;
        return new Date(a.date).getTime() - new Date(b.date).getTime();
      },
      render: (date: string) => date ? new Date(date).toLocaleDateString() : '-',
    },
    {
      title: 'Quotation #',
      dataIndex: 'quotation',
      key: 'quotation',
      width: 150,
      sorter: (a: Invoice, b: Invoice) => (a.quotation?.quotation_number || '').localeCompare(b.quotation?.quotation_number || ''),
      render: (quotation: any) => quotation?.quotation_number || '-',
    },
    {
      title: 'Customer',
      dataIndex: 'customer',
      key: 'customer',
      width: 180,
      sorter: (a: Invoice, b: Invoice) => (a.customer?.name || '').localeCompare(b.customer?.name || ''),
      render: (customer: any) => customer?.name || '-',
    },
    {
      title: 'Agent',
      dataIndex: 'agent',
      key: 'agent',
      width: 160,
      sorter: (a: Invoice, b: Invoice) => (a.agent?.name || '').localeCompare(b.agent?.name || ''),
      render: (agent: any) => agent?.name || '-',
    },
    {
      title: 'Team',
      dataIndex: 'team',
      key: 'team',
      width: 160,
      sorter: (a: Invoice, b: Invoice) => (a.team?.name || '').localeCompare(b.team?.name || ''),
      render: (team: any) => team?.name || '-',
    },
    {
      title: 'Staff',
      dataIndex: 'staff',
      key: 'staff',
      width: 160,
      sorter: (a: Invoice, b: Invoice) => (a.staff?.name || '').localeCompare(b.staff?.name || ''),
      render: (staff: any) => staff?.name || '-',
    },
  ];

  const {
    columnConfig,
    visibleColumns,
    visibleKeys,
    handleColumnVisibilityChange,
  } = useColumnVisibility(baseColumns);

  const { columns: resizableColumns } = useResizableColumns(visibleColumns);

  const memoizedColumns = useMemo(
    () =>
      resizableColumns.map((col) => ({
        ...col,
        width: col.width, // Preserve width
        onHeaderCell: col.onHeaderCell, // Preserve onHeaderCell for resizing
        // Provide better search values for relation columns
        getSearchValue:
          col.dataIndex === 'invoice_number'
            ? (record: Invoice) => record.invoice_number
            : col.dataIndex === 'date'
            ? (record: Invoice) => record.date
            : col.dataIndex === 'quotation'
            ? (record: Invoice) => record.quotation?.quotation_number
            : col.dataIndex === 'customer'
            ? (record: Invoice) => record.customer?.name
            : col.dataIndex === 'agent'
            ? (record: Invoice) => record.agent?.name
            : col.dataIndex === 'team'
            ? (record: Invoice) => record.team?.name
            : col.dataIndex === 'staff'
            ? (record: Invoice) => record.staff?.name
            : (undefined as unknown as (record: Invoice) => string | number | null | undefined),
      })),
    [resizableColumns]
  );

  const { columnsWithSearch, filteredData } = useTableColumnSearch<Invoice>(invoices, memoizedColumns as any);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title level={3} style={{ margin: 0 }}>
            Invoices
          </Title>
          <Space>
            <ColumnVisibilityToggle
              columns={columnConfig}
              visibleKeys={visibleKeys}
              onChange={handleColumnVisibilityChange}
            />
          </Space>
        </div>

        <Table
          columns={columnsWithSearch}
          dataSource={filteredData}
          rowKey="id"
          loading={loading}
          pagination={false}
          scroll={{ y: 'calc(100vh - 400px)' }}
          components={{
            header: {
              cell: ResizableTableHeader,
            },
          }}
          rowSelection={{
            selectedRowKeys,
            onChange: (keys) => setSelectedRowKeys(keys),
            hideSelectAll: true,
            columnWidth: 0,
            renderCell: () => null,
          }}
          footer={() => (
            <div style={{ textAlign: 'left', padding: '8px 0' }}>
              Total: {filteredData.length} {filteredData.length === 1 ? 'invoice' : 'invoices'}
            </div>
          )}
          onRow={(record) => ({
            onClick: () => setSelectedRowKeys([record.id]),
            onDoubleClick: canView ? () => navigate(`/invoices/${record.id}/edit`) : undefined,
            style: { cursor: canView ? 'pointer' : 'default' },
          })}
        />
    </div>
  );
};
